import { attr, FASTElement, html } from "@microsoft/fast-element";

// CSS syntax patterns
const cssPatterns = {
	"css-property": /(?:^|\s)([a-z-]+)(?=\s*:)/gm,
	"css-function": /([a-z-]+)(\()/g,
	"css-color":
		/#[0-9a-fA-F]{3,8}|rgb\([^)]+\)|rgba\([^)]+\)|hsl\([^)]+\)|hsla\([^)]+\)/g,
	"css-unit":
		/\b\d+(?:\.\d+)?(?:px|em|rem|%|vh|vw|vmin|vmax|pt|pc|in|cm|mm|ex|ch|lh|rlh|vi|vb|deg|rad|grad|turn|s|ms|Hz|kHz|dpi|dpcm|dppx)\b/g,
	"css-selector": /^[^{]*(?=\s*\{)/gm,
	"css-string": /(["'])[^"']*\1/g,
	"css-comment": /\/\*[\s\S]*?\*\//g,
};

// W3C Formal Syntax patterns
const syntaxPatterns = {
	"syntax-type": /<[^>]+>/g,
	"syntax-combinator": /[|&[\]?*+!]+/g,
	"syntax-string": /'[^']*'/g,
};

// JavaScript syntax patterns
const jsPatterns = {
	"js-keyword":
		/\b(?:const|let|var|function|return|if|else|for|while|do|switch|case|break|continue|new|class|extends|import|from|export|default|try|catch|finally|throw|await|async|typeof|instanceof|in|of|null|true|false|undefined)\b/g,
	"js-number": /\b\d+(?:\.\d+)?\b/g,
	"js-function":
		/\b(?!if\b|for\b|while\b|switch\b|catch\b|function\b)([A-Za-z_$][\w$]*)(?=\s*\()/g,
	"js-property": /(?<=\.)[A-Za-z_$][\w$]*/g,
	"js-operator": /[+\-*/%=<>!&|^~?]+/g,
	"js-punctuation": /[(){}[\];,.]/g,
	"js-string": /`(?:\\[\s\S]|[^\\`])*`|"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g,
	"js-comment": /\/\*[\s\S]*?\*\/|\/\/[^\n]*/g,
};

export class CodeBlock extends FASTElement {
	@attr language: string;
	ranges: Map<string, Range[]> = new Map();

	connectedCallback() {
		super.connectedCallback();
		requestIdleCallback(() => {
			this.highlight();
		});
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.clearHighlights();
	}

	clearHighlights() {
		for (const [name, ranges] of this.ranges) {
			const highlight = CSS.highlights.get(name);
			if (highlight) {
				ranges.forEach((r) => {
					highlight.delete(r);
				});
			}
		}
		this.ranges.clear();
	}

	highlight() {
		const lang = this.language || "";
		const isJS =
			lang === "javascript" ||
			lang === "js" ||
			this.classList.contains("language-javascript") ||
			this.classList.contains("language-js");
		const isSyntax = lang === "syntax";
		const patterns = isJS
			? jsPatterns
			: isSyntax
				? syntaxPatterns
				: cssPatterns;

		const slot = this.shadowRoot?.querySelector("slot");
		if (!(slot instanceof HTMLSlotElement)) return;

		const textNode = slot
			.assignedNodes()
			.find((node) => node.nodeType === Node.TEXT_NODE);
		if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return;

		const text = textNode.textContent;

		for (const [name, pattern] of Object.entries(patterns)) {
			const regex = new RegExp(pattern.source, pattern.flags);
			const newRanges = [];

			for (const match of text.matchAll(regex)) {
				const range = new Range();
				range.setStart(textNode, match.index);
				range.setEnd(textNode, match.index + match[0].length);
				newRanges.push(range);
			}

			if (newRanges.length > 0) {
				if (!CSS.highlights.has(name)) {
					CSS.highlights.set(name, new Highlight());
				}
				const highlight = CSS.highlights.get(name);
				newRanges.forEach((r) => {
					highlight.add(r);
				});

				if (!this.ranges.has(name)) {
					this.ranges.set(name, []);
				}
				this.ranges.get(name).push(...newRanges);
			}
		}
	}
}

CodeBlock.define({
	name: "code-block",
	template: html`
		<link href="/assets/css/code-block.css" rel="stylesheet" />
		<pre class="code-block"><code><slot></slot></code></pre>
	`,
});
